import type { HrefOptions } from "@uirouter/core";

import * as siRoutes from "app/pages/sales-intelligence/constants/routes";
import {
    COMPANY_ANALYSIS_DIGITAL_ASSETS,
    CRM_LIST_PAGE_ROUTE,
    STATIC_LIST_PAGE_ROUTE,
} from "app/pages/sales-intelligence/constants/routes";
import * as slRoutes from "app/pages/sales-intelligence/constants/sl_routes";
import type { ApiPageType } from "app/pages/sales-intelligence/modules/manage-integrations/types/api-page-type";
import type { RequestAdminFeatureBase } from "app/pages/sales-intelligence/types/requestAdminFeature";
import type { IRouterState } from "single-spa/react-app/router/IRouterState";

import type { Integration } from "../../sub-modules/management/types";
import type {
    CrmListRoutingParams,
    CrmManagementRoutingParams,
    StaticListRoutingParams,
} from "../../sub-modules/opportunities/types/routing";

import type {
    SavedSearchRoutingParams,
    SiNavigator,
    SiNavigatorDeps,
    SuperAgentRouteParams,
    PlaybookCreateRoutingParams,
    PlaybookEditRoutingParams,
    TeamDetailRoutingParams,
} from "./types";

const createSiNavigator = ({ navigator }: SiNavigatorDeps): SiNavigator => {
    const siNavigator: SiNavigator = {
        get isAbstractAdvancedSearchPage(): boolean {
            const { parent } = navigator.current();
            return parent === siRoutes.FIND_LEADS_SAVED_SEARCH_ABSTRACT_ROUTE;
        },
        isAdvancedSearchPage(state?: IRouterState): boolean {
            return navigator.isCurrentStateName(siRoutes.FIND_LEADS_SEARCH_ROUTE, state);
        },
        goToAdvancedSearchPage(options, params = {}) {
            return navigator.go(siRoutes.FIND_LEADS_SEARCH_ROUTE, params, options);
        },

        get isSavedSearchPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.FIND_LEADS_SAVED_SEARCH_ROUTE);
        },
        goToSavedSearchPageById(params: SavedSearchRoutingParams, options) {
            return navigator.go(siRoutes.FIND_LEADS_SAVED_SEARCH_ROUTE, params, options);
        },
        createSavedSearchLink(params: SavedSearchRoutingParams, options?: HrefOptions): string {
            return navigator.href(siRoutes.FIND_LEADS_SAVED_SEARCH_ROUTE, params, options);
        },
        getSavedSearchPageParams(): SavedSearchRoutingParams | undefined {
            if (!siNavigator.isSavedSearchPage) {
                return;
            }

            return navigator.getParams() as SavedSearchRoutingParams;
        },

        get isHomePage(): boolean {
            return navigator.isCurrentStateName(siRoutes.HOME_PAGE_ROUTE);
        },

        get isAccountSignalsManagementPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE);
        },
        goToAccountSignalsManagementPage() {
            return navigator.go(siRoutes.ACCOUNT_SIGNALS_MANAGEMENT_PAGE_ROUTE);
        },

        get isIntentSignalsManagementPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE);
        },
        goToIntentSignalsManagementPage() {
            return navigator.go(siRoutes.INTENT_SIGNALS_MANAGEMENT_PAGE_ROUTE);
        },

        get isDownloadExtensionPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.DOWNLOAD_EXTENSION_PAGE_ROUTE);
        },

        get isTeamsManagementPage(): boolean {
            return navigator.isCurrentStateName(slRoutes.TEAMS_MANAGEMENT_ROUTE);
        },

        get isStaticListPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.STATIC_LIST_PAGE_ROUTE);
        },

        get isCrmListPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.CRM_LIST_PAGE_ROUTE);
        },

        get getCrmListPageIntegrationName(): Integration {
            return navigator.getParams().crm as Integration;
        },

        goToStaticListPage(params, options) {
            return navigator.go(siRoutes.STATIC_LIST_PAGE_ROUTE, params, options);
        },

        createStaticListLink(params: StaticListRoutingParams, options?: HrefOptions): string {
            return navigator.href(STATIC_LIST_PAGE_ROUTE, params, options);
        },

        goToCrmListPage(params, options) {
            return navigator.go(siRoutes.CRM_LIST_PAGE_ROUTE, params, options);
        },

        createCrmListLink(params: CrmListRoutingParams, options?: HrefOptions): string {
            return navigator.href(CRM_LIST_PAGE_ROUTE, params, options);
        },

        get isAccountOverviewPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.ACCOUNT_OVERVIEW_COMPANY);
        },

        goToListsHomePage(options) {
            return navigator.go(siRoutes.MY_LISTS_PAGE_ROUTE, undefined, options);
        },

        goToManagementPage(options) {
            return navigator.go(siRoutes.MANAGEMENT_ROUTE, options);
        },

        goToCrmManagementPage(options: CrmManagementRoutingParams) {
            return navigator.go(slRoutes.MANAGE_INTEGRATION_ROUTE[options.crm]);
        },

        goToCrmIntegrationsPage() {
            return navigator.go(slRoutes.MANAGEMENT_CRM_INTEGRATIONS_ROUTE);
        },

        goToAPIsWebhooksPage() {
            return navigator.go(slRoutes.MANAGEMENT_APIS_AND_WEBHOOKS_ROUTE);
        },

        goToMCPServersPage() {
            return navigator.go(slRoutes.MANAGEMENT_MCP);
        },

        goToGatewayPage(featureName: RequestAdminFeatureBase | ApiPageType) {
            return navigator.go(slRoutes.MANAGEMENT_GATEWAY, { featureName });
        },

        goToCrmEnrichmentManagementPage({ crm }: CrmManagementRoutingParams) {
            return navigator.go(slRoutes.MANAGE_CRM_ENRICHMENT_ROUTE[crm]);
        },

        goToCrmEnrichmentJobEditPage({ jobId, crm }) {
            return navigator.go(slRoutes.CRM_ENRICHMENT_JOB_EDIT_ROUTE[crm], { id: jobId });
        },

        goToCrmEnrichmentLogsPage({ jobId, crm }) {
            return navigator.go(slRoutes.CRM_ENRICHMENT_LOGS_ROUTE[crm], { jobId });
        },

        goToExportIntegrationPage(options: CrmManagementRoutingParams) {
            return navigator.go(slRoutes.EXPORT_INTEGRATION_ROUTE[options.crm]);
        },

        goToExtensionManagementPage() {
            return navigator.go(slRoutes.EXTENSION_MANAGEMENT_MANAGE_ROUTE);
        },

        goToTeamsManagementPage() {
            return navigator.go(slRoutes.TEAMS_MANAGEMENT_ROUTE);
        },

        goToTeamDetailPage(params: TeamDetailRoutingParams) {
            return navigator.go(slRoutes.TEAM_DETAIL_ROUTE, params);
        },

        createTeamDetailLink(params: TeamDetailRoutingParams, options?: HrefOptions) {
            return navigator.href(slRoutes.TEAM_DETAIL_ROUTE, params, options);
        },

        goToPlaybooksPage() {
            return navigator.go(slRoutes.PLAYBOOKS_ROUTE);
        },

        goToPlaybookCreatePage(params: PlaybookCreateRoutingParams) {
            return navigator.go(slRoutes.PLAYBOOK_CREATE_ROUTE, { shared: params.isSharedWithAccount });
        },

        goToPlaybookEditPage(params: PlaybookEditRoutingParams) {
            return navigator.go(slRoutes.PLAYBOOK_EDIT_ROUTE, params);
        },

        goToSuppressionManagementPage() {
            return navigator.go(slRoutes.SL_ROUTES.upload);
        },

        goToManagementSignalsApiPage() {
            return navigator.go(slRoutes.MANAGEMENT_SIGNALS_API_ROUTE);
        },

        goToManagementSignalsRestApiPage() {
            return navigator.go(slRoutes.MANAGEMENT_SIGNALS_REST_API_ROUTE);
        },

        goToManagementSavedSearchWebhooksPage() {
            return navigator.go(slRoutes.MANAGEMENT_SAVED_SEARCH_WEBHOOKS_ROUTE);
        },

        get isCompanyAnalysisPage(): boolean {
            const { parent } = navigator.current();
            return parent === siRoutes.COMPANY_ANALYSIS_KEY;
        },

        get isSuperAgentPage(): boolean {
            return navigator.isCurrentStateName(siRoutes.AI_SUPER_AGENT);
        },

        goToCompanyAnalysisOverviewPage(key: string) {
            return navigator.go(siRoutes.COMPANY_ANALYSIS_OVERVIEW, { key });
        },
        getCompanyAnalysisOverviewLink(params, options) {
            return navigator.href(siRoutes.COMPANY_ANALYSIS_OVERVIEW, params, options);
        },

        goToCompanyAnalysisSignalsPage(key: string) {
            return navigator.go(siRoutes.COMPANY_ANALYSIS_SIGNALS, { key });
        },

        getCompanyAnalysisSignalsPage(domain: string) {
            return navigator.href(siRoutes.COMPANY_ANALYSIS_SIGNALS, {
                key: domain,
            });
        },

        goToCompanyDigitalAssetsPage(domain) {
            return navigator.go(COMPANY_ANALYSIS_DIGITAL_ASSETS, { key: domain });
        },

        goToWebsiteAnalysisPerformancePage(params) {
            return navigator.go(siRoutes.WEBSITE_ANALYSIS_PERFORMANCE, params);
        },
        getWebsiteAnalysisPerformanceLink(params) {
            return navigator.href(siRoutes.WEBSITE_ANALYSIS_PERFORMANCE, params);
        },

        getToCompanyDigitalAssetsPageLink(domain: string): string {
            return navigator.href(COMPANY_ANALYSIS_DIGITAL_ASSETS, { key: domain });
        },

        getWebsiteAnalysisTechnologiesLink(domain, category) {
            return navigator.href(siRoutes.ACCOUNT_REVIEW_TECHNOLOGIES_ROUTE, { key: domain, category });
        },

        getWebsiteAnalysisDisplayAdNetworksLink(params) {
            return navigator.href(siRoutes.WEBSITE_ANALYSIS_MARKETING_DISPLAY_OVERVIEW, params);
        },

        goToAIAgentMeetingRoom() {
            return navigator.go(siRoutes.AI_AGENT_MEETING_ROOT);
        },

        goToAIAgentCraftEmail() {
            return navigator.go(siRoutes.AI_AGENT_EMAIL);
        },

        goToEnrichmentPage() {
            return navigator.go(siRoutes.ENRICHMENT_PAGE_ROUTE);
        },

        goToSuperAgentPage(options?: SuperAgentRouteParams) {
            return navigator.go(siRoutes.AI_SUPER_AGENT, options);
        },

        goToScoreSettingsPage() {
            return navigator.go(siRoutes.SCORE_SETTINGS_PAGE_ROUTE);
        },

        getScoreSettingsPageLink() {
            return navigator.href(siRoutes.SCORE_SETTINGS_PAGE_ROUTE);
        },
    };

    return siNavigator;
};

export default createSiNavigator;
